import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import apiClient from '../../service/apiClient';
import FileUpload from '../form/FileUpload';
import BigButton from '../button/BigButton';
import {useProductStore} from "../../store/useProduct";

interface DefaultImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  productId: string;
}

/**
 * @author assistant
 * @date 2025.01.04
 * @description 기본 이미지 변경 모달
 */
const DefaultImageModal = ({ isOpen, onClose, title, productId }: DefaultImageModalProps) => {
  const { getProductBasicImage, basicImage, postProductBasicImageUpdate } = useProductStore();

  // 새로운 이미지 상태
  const [newThumbnail, setNewThumbnail] = useState<{file: File; id: string} | null>(null);
  const [newInline, setNewInline] = useState<{file: File; id: string} | null>(null);
  const [newEndImage, setNewEndImage] = useState<{file: File; id: string} | null>(null);
  
  // 기존 기본 이미지 URL 상태
  const [existingDefaultThumbnail, setExistingDefaultThumbnail] = useState<string>('');
  const [existingDefaultInline, setExistingDefaultInline] = useState<string>('');
  const [existingDefaultEnd, setExistingDefaultEnd] = useState<string>('');
  
  // 업로드용 파일 상태
  const [thumbnailFiles, setThumbnailFiles] = useState<File[]>([]);
  const [inlineFiles, setInlineFiles] = useState<File[]>([]);
  const [endFiles, setEndFiles] = useState<File[]>([]);

  // A4 비율 검증 함수
  const validateA4Ratio = (width: number, height: number): boolean => {
    const ratio = height / width;
    const a4Ratio = 1.414; // A4 비율 (297/210)
    const tolerance = 0.05; // 5% 오차 허용
    return Math.abs(ratio - a4Ratio) <= tolerance;
  };

  // 썸네일 업로드 처리
  useEffect(() => {
    const uploadThumbnail = async () => {
      if (thumbnailFiles.length > 0) {
        const file = thumbnailFiles[0];
        const img = new Image();
        const reader = new FileReader();

        reader.onloadend = (e) => {
          img.onload = async () => {
            if (!validateA4Ratio(img.width, img.height)) {
              toast.error('이미지가 A4 비율(210:297)이 아닙니다. A4 비율의 이미지를 업로드해주세요.');
              setThumbnailFiles([]);
              return;
            }

            const formData = new FormData();
            formData.append('file', file);

            try {
              const response = await apiClient.post('/upload-file/upload', formData);
              setNewThumbnail({ file, id: response.data.id });
              setThumbnailFiles([]);
            } catch (error) {
              console.error('이미지 업로드 실패:', error);
              toast.error('이미지 업로드를 실패했습니다.');
            }
          };
          img.src = e.target?.result as string;
        };
        reader.readAsDataURL(file);
      }
    };
    uploadThumbnail();
  }, [thumbnailFiles]);

  // 속지 업로드 처리
  useEffect(() => {
    const uploadInline = async () => {
      if (inlineFiles.length > 0) {
        const file = inlineFiles[0];
        const img = new Image();
        const reader = new FileReader();

        reader.onloadend = (e) => {
          img.onload = async () => {
            if (!validateA4Ratio(img.width, img.height)) {
              toast.error('이미지가 A4 비율(210:297)이 아닙니다. A4 비율의 이미지를 업로드해주세요.');
              setInlineFiles([]);
              return;
            }

            const formData = new FormData();
            formData.append('file', file);

            try {
              const response = await apiClient.post('/upload-file/upload', formData);
              setNewInline({ file, id: response.data.id });
              setInlineFiles([]);
            } catch (error) {
              console.error('이미지 업로드 실패:', error);
              toast.error('이미지 업로드를 실패했습니다.');
            }
          };
          img.src = e.target?.result as string;
        };
        reader.readAsDataURL(file);
      }
    };
    uploadInline();
  }, [inlineFiles]);

  // 맺음말 업로드 처리
  useEffect(() => {
    const uploadEnd = async () => {
      if (endFiles.length > 0) {
        const file = endFiles[0];
        const img = new Image();
        const reader = new FileReader();

        reader.onloadend = (e) => {
          img.onload = async () => {
            if (!validateA4Ratio(img.width, img.height)) {
              toast.error('이미지가 A4 비율(210:297)이 아닙니다. A4 비율의 이미지를 업로드해주세요.');
              setEndFiles([]);
              return;
            }

            const formData = new FormData();
            formData.append('file', file);

            try {
              const response = await apiClient.post('/upload-file/upload', formData);
              setNewEndImage({ file, id: response.data.id });
              setEndFiles([]);
            } catch (error) {
              console.error('이미지 업로드 실패:', error);
              toast.error('이미지 업로드를 실패했습니다.');
            }
          };
          img.src = e.target?.result as string;
        };
        reader.readAsDataURL(file);
      }
    };
    uploadEnd();
  }, [endFiles]);

  // 모달이 열릴 때 기본 이미지 로드
  useEffect(() => {
    if (isOpen && productId) {
      getProductBasicImage(productId);
    }
  }, [isOpen, productId, getProductBasicImage]);

  // basicImage가 로드되면 상태 업데이트
  useEffect(() => {
    if (basicImage) {
      setExistingDefaultThumbnail(basicImage.thumbnailFile?.fileFullUrl || '');
      setExistingDefaultInline(basicImage.inlineFile?.fileFullUrl || '');
      setExistingDefaultEnd(basicImage.endFile?.fileFullUrl || '');
    }
  }, [basicImage]);

  // 모달 닫힐 때 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setNewThumbnail(null);
      setNewInline(null);
      setNewEndImage(null);
      setExistingDefaultThumbnail('');
      setExistingDefaultInline('');
      setExistingDefaultEnd('');
    }
  }, [isOpen]);

  // 저장 처리
  const handleSave = async () => {
    // 3개 이미지가 모두 없으면 종료
    if (!newThumbnail && !newInline && !newEndImage) {
      toast.warning('변경할 이미지가 없습니다.');
      return;
    }
    
    try {
      // API 호출 (기본 이미지 업데이트)
      await postProductBasicImageUpdate(productId, newThumbnail?.id, newInline?.id, newEndImage?.id)
      onClose();
    } catch (error) {
      console.error('기본 이미지 변경 실패:', error);
      toast.error('기본 이미지 변경에 실패했습니다.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg flex flex-col" style={{ width: '900px', maxHeight: '85vh' }}>
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="!grid !grid-cols-3 !gap-6">
            {/* 대표 이미지 */}
            <div className="!space-y-2">
              <div className="!flex !items-center !justify-between !mb-2">
                <label className="!text-sm !font-medium !text-gray-700">대표 이미지</label>
                <label className="!flex !items-center !gap-2">
                </label>
              </div>
              {existingDefaultThumbnail && !newThumbnail ? (
                <div className="!relative">
                  <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                    <img
                      src={existingDefaultThumbnail}
                      alt="thumbnail"
                      className="!w-full !h-full !object-contain"
                    />
                  </div>
                  <div className="!absolute !top-2 !left-2 !px-2 !py-1 !bg-gray-600 !text-white !text-xs !rounded">
                    기본 이미지
                  </div>
                  <button
                    className="!absolute !bottom-2 !right-2 !px-3 !py-1.5 !bg-blue-500 !text-white !text-xs !rounded hover:!bg-blue-600"
                    onClick={() => setExistingDefaultThumbnail('')}
                    type="button"
                  >
                    변경
                  </button>
                </div>
              ) : newThumbnail ? (
                <div className="!relative">
                  <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                    <img
                      src={URL.createObjectURL(newThumbnail.file)}
                      alt="thumbnail"
                      className="!w-full !h-full !object-contain"
                    />
                  </div>
                  <button
                    className="!absolute !top-2 !right-2 !px-3 !py-1.5 !bg-red-500 !text-white !text-xs !rounded hover:!bg-red-600"
                    onClick={() => {
                      setNewThumbnail(null);
                    }}
                    type="button"
                  >
                    삭제
                  </button>
                </div>
              ) : (
                <div className="!border-2 !border-dashed !border-gray-300 !rounded-lg !flex !items-center !justify-center hover:!border-gray-400 !transition-colors" style={{aspectRatio: '210/297'}}>
                  <FileUpload setFiles={setThumbnailFiles} accept="image/*" multiple={false} />
                </div>
              )}
            </div>

            {/* 속지 이미지 */}
            <div className="!space-y-2">
              <div className="!flex !items-center !justify-between !mb-2">
                <label className="!text-sm !font-medium !text-gray-700">속지 이미지</label>
              </div>
              {existingDefaultInline && !newInline ? (
                <div className="!relative">
                  <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                    <img
                      src={existingDefaultInline}
                      alt="inline"
                      className="!w-full !h-full !object-contain"
                    />
                  </div>
                  <div className="!absolute !top-2 !left-2 !px-2 !py-1 !bg-gray-600 !text-white !text-xs !rounded">
                    기본 이미지
                  </div>
                  <button
                    className="!absolute !bottom-2 !right-2 !px-3 !py-1.5 !bg-blue-500 !text-white !text-xs !rounded hover:!bg-blue-600"
                    onClick={() => setExistingDefaultInline('')}
                    type="button"
                  >
                    변경
                  </button>
                </div>
              ) : newInline ? (
                <div className="!relative">
                  <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                    <img
                      src={URL.createObjectURL(newInline.file)}
                      alt="inline"
                      className="!w-full !h-full !object-contain"
                    />
                  </div>
                  <button
                    className="!absolute !top-2 !right-2 !px-3 !py-1.5 !bg-red-500 !text-white !text-xs !rounded hover:!bg-red-600"
                    onClick={() => {
                      setNewInline(null);
                    }}
                    type="button"
                  >
                    삭제
                  </button>
                </div>
              ) : (
                <div className="!border-2 !border-dashed !border-gray-300 !rounded-lg !flex !items-center !justify-center hover:!border-gray-400 !transition-colors" style={{aspectRatio: '210/297'}}>
                  <FileUpload setFiles={setInlineFiles} accept="image/*" multiple={false} />
                </div>
              )}
            </div>

            {/* 맺음말 이미지 */}
            <div className="!space-y-2">
              <div className="!flex !items-center !justify-between !mb-2">
                <label className="!text-sm !font-medium !text-gray-700">맺음말 이미지</label>
              </div>
              {existingDefaultEnd && !newEndImage ? (
                <div className="!relative">
                  <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                    <img
                      src={existingDefaultEnd}
                      alt="endImage"
                      className="!w-full !h-full !object-contain"
                    />
                  </div>
                  <div className="!absolute !top-2 !left-2 !px-2 !py-1 !bg-gray-600 !text-white !text-xs !rounded">
                    기본 이미지
                  </div>
                  <button
                    className="!absolute !bottom-2 !right-2 !px-3 !py-1.5 !bg-blue-500 !text-white !text-xs !rounded hover:!bg-blue-600"
                    onClick={() => setExistingDefaultEnd('')}
                    type="button"
                  >
                    변경
                  </button>
                </div>
              ) : newEndImage ? (
                <div className="!relative">
                  <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                    <img
                      src={URL.createObjectURL(newEndImage.file)}
                      alt="endImage"
                      className="!w-full !h-full !object-contain"
                    />
                  </div>
                  <button
                    className="!absolute !top-2 !right-2 !px-3 !py-1.5 !bg-red-500 !text-white !text-xs !rounded hover:!bg-red-600"
                    onClick={() => {
                      setNewEndImage(null);
                    }}
                    type="button"
                  >
                    삭제
                  </button>
                </div>
              ) : (
                <div className="!border-2 !border-dashed !border-gray-300 !rounded-lg !flex !items-center !justify-center hover:!border-gray-400 !transition-colors" style={{aspectRatio: '210/297'}}>
                  <FileUpload setFiles={setEndFiles} accept="image/*" multiple={false} />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 border-t">
          <BigButton type="submit" text="기본 이미지 변경" onClick={handleSave} />
        </div>
      </div>
    </div>
  );
};

export default DefaultImageModal;